import React, { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material'
import API from '../../services/API'
import { toDateInput } from '../kit/helpers'
import { defaultQuotedSubscriptionForm, enterprisePtsPriceOptions } from './tenantHelpers'

// Creates a real Stripe subscription on a quoted Enterprise+ price and moves
// the tenant onto Enterprise. Pre-fills from the existing contract where it can.
// (For a tenant that already has a subscription, staff change it by hand in
// Stripe and record it from the Commercial usage tab instead.)
const QuotedSubscriptionDialog = ({ open, tenant, contract, onClose, onStarted }) => {
  const [form, setForm] = useState(defaultQuotedSubscriptionForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    setError('')
    setForm({
      ...defaultQuotedSubscriptionForm,
      quoteReference: contract?.quote?.quoteReference || '',
      acceptedBy: contract?.quote?.acceptedBy || '',
      contractStartDate: toDateInput(contract?.contractStartDate) || '',
      renewalDate: toDateInput(contract?.renewalDate) || '',
      accountManager: contract?.accountManager || '',
      notes: contract?.notes || '',
    })
  }, [open, contract])

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }))

  const start = async () => {
    setSaving(true)
    setError('')
    const res = await API.post(`/admincrm/tenants/${tenant._id}/quoted-subscription`, form)
    setSaving(false)
    if (res.status !== 200) {
      setError(res.data?.message || res.data?.error || 'Could not start the subscription.')
      return
    }
    onStarted(res.data.tenant)
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Start Enterprise+ subscription</DialogTitle>
      <DialogContent>
        <Stack gap={2} paddingTop={1}>
          <Alert severity="warning">
            This creates a real Stripe subscription and updates this tenant to Enterprise with the
            selected PTS allowance. It does not cancel any existing subscription.
          </Alert>
          {tenant.stripeSubscriptionId && (
            <Alert severity="error">
              This tenant already has a Stripe subscription. Cancel or manage the existing
              subscription before starting a quoted one.
            </Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          <FormControl fullWidth size="small">
            <InputLabel>Enterprise+ price</InputLabel>
            <Select
              value={form.lookupKey}
              label="Enterprise+ price"
              onChange={(e) => set('lookupKey', e.target.value)}
            >
              {enterprisePtsPriceOptions.map((option) => (
                <MenuItem key={option.lookupKey} value={option.lookupKey}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={form.collectionMethod === 'send_invoice' ? 8 : 12}>
              <FormControl fullWidth size="small">
                <InputLabel>Collection method</InputLabel>
                <Select
                  value={form.collectionMethod}
                  label="Collection method"
                  onChange={(e) => set('collectionMethod', e.target.value)}
                >
                  <MenuItem value="send_invoice">Send invoice</MenuItem>
                  <MenuItem value="charge_automatically">Charge automatically</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {form.collectionMethod === 'send_invoice' && (
              <Grid item xs={12} md={4}>
                <TextField
                  label="Days until due"
                  size="small"
                  fullWidth
                  type="number"
                  value={form.daysUntilDue}
                  onChange={(e) => set('daysUntilDue', e.target.value)}
                />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField label="Quote reference" size="small" fullWidth value={form.quoteReference} onChange={(e) => set('quoteReference', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Accepted by" size="small" fullWidth value={form.acceptedBy} onChange={(e) => set('acceptedBy', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Contract start" size="small" fullWidth type="date" InputLabelProps={{ shrink: true }} value={form.contractStartDate} onChange={(e) => set('contractStartDate', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Renewal date" size="small" fullWidth type="date" InputLabelProps={{ shrink: true }} value={form.renewalDate} onChange={(e) => set('renewalDate', e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Account manager" size="small" fullWidth value={form.accountManager} onChange={(e) => set('accountManager', e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Notes" size="small" fullWidth multiline minRows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={start}
          disabled={saving || !!tenant.stripeSubscriptionId}
        >
          {saving ? 'Starting…' : 'Start subscription'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default QuotedSubscriptionDialog
